import { cn } from '@/lib/utils'

/**
 * A skeleton loading component that displays a placeholder with pulse animation.
 * Used to indicate content is loading and preserve layout during data fetching.
 * 
 * @param className - Additional CSS classes to apply for custom sizing and spacing
 * @param props - Additional HTML div attributes
 * 
 * @example
 * ```tsx
 * // Basic skeleton
 * <Skeleton className="h-4 w-[250px]" />
 * 
 * // Skeleton for avatar
 * <Skeleton className="h-12 w-12 rounded-full" />
 * 
 * // Skeleton for text lines
 * <div className="space-y-2">
 *   <Skeleton className="h-4 w-[250px]" />
 *   <Skeleton className="h-4 w-[200px]" />
 * </div>
 * 
 * // Skeleton for card content
 * <div className="flex items-center space-x-4">
 *   <Skeleton className="h-12 w-12 rounded-full" />
 *   <div className="space-y-2">
 *     <Skeleton className="h-4 w-[250px]" />
 *     <Skeleton className="h-4 w-[200px]" />
 *   </div>
 * </div>
 * ```
 */
function Skeleton({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="skeleton"
      className={cn('bg-accent animate-pulse rounded-md', className)}
      {...props}
    />
  )
}

export { Skeleton }
